import { LinearProgress, TableCell, TableRow, Typography } from '@mui/material';
import { SubmissionEntry } from 'actions/challenge';
import MUIDataTable, {
  MUIDataTableOptions,
  MUIDataTableState,
} from 'mui-datatables';
import './index.css';
import { TreeItem, TreeView } from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MUISortOptions } from 'mui-datatables';
type SubmissionsListProps = {
  data: SubmissionEntry[];
  total?: number;
  metrics: string[];
  showProgress: boolean;
  prependColumns?: any[];
  rowsPerPage?: number;
  dataHandler?: (tableState: MUIDataTableState, sortKey: string) => void;
  prependData?: (d: SubmissionEntry) => Array<any>;
};
const SubmissionsList = ({
  data,
  metrics,
  showProgress,
  prependColumns = [],
  prependData,
  rowsPerPage = 10,
  dataHandler,
  total,
}: SubmissionsListProps) => {
  const hasUser = data[0] && (data[0] as any).user;
  let columns: any[];
  if (hasUser) {
    columns = [
      {
        name: 'Submitter',
        options: {
          filter: false,
          sort: false,
        },
      },
      {
        name: 'Submission Name',
        options: {
          filter: false,
        },
      },
      {
        name: 'Team Name',
        options: {
          filter: false,
        },
      },
      {
        name: 'Tags',
        options: {
          filter: false,
          sort: false,
        },
      },
      {
        name: 'Submission Date',
        options: {
          filter: false,
        },
      },
      {
        name: 'Status',
        options: {
          filter: false,
          customBodyRender: (
            value: string,
            tableMeta: any,
            updateValue: any
          ) => {
            return <div className={`status-${value}`}>{value}</div>;
          },
        },
      },
    ];
  } else {
    columns = [
      {
        name: 'Submission Name',
        options: {
          filter: false,
        },
      },
      {
        name: 'Team',
        options: {
          filter: false,
        },
      },
      {
        name: 'Tags',
        options: {
          filter: true,
        },
      },
      {
        name: 'Submission Date',
        options: {
          filter: false,
        },
      },
      {
        name: 'Status',
        options: {
          filter: true,
          customBodyRender: (
            value: string,
            tableMeta: any,
            updateValue: any
          ) => {
            return <div className={`status-${value}`}>{value}</div>;
          },
        },
      },
    ];
  }
  columns = [...prependColumns, ...columns];

  const hiddenMetricsIds = new Set();

  metrics.forEach((metric, idx) => {
    if (metric.match('__hidden_')) {
      hiddenMetricsIds.add(idx);
      return;
    }
    columns.push({
      name: metric,
      options: {
        filter: false,
      },
    });
  });

  const toHex = (x: number) => {
    let s = Number(x).toString(16);
    if (s.length < 2) {
      s = '0' + s;
    }
    return s;
  };

  let tableData = data.map((d) => {
    let tags = 'None';
    const results = [];
    for (let i = 0; i < d.results.length; i++) {
      if (!hiddenMetricsIds.has(i)) {
        results.push(d.results[i]);
      }
    }
    if (d.tags && tags.length > 0) {
      tags = d.tags.join(', ');
    }
    let data = [];
    if (hasUser) {
      data = [
        (d as any).user.name,
        d.name,
        d.teamName ? d.teamName : 'None',
        tags,
        new Date(d.submissionDate).toLocaleString(),
        d.status,
        ...results,
      ];
    } else {
      data = [
        d.name,
        d.teamName ? d.teamName : 'None',
        tags,
        new Date(d.submissionDate).toLocaleString(),
        d.status,
        ...results,
      ];
    }
    if (prependData) {
      data = [...prependData(d), ...data];
    }
    return data;
  });

  const options: MUIDataTableOptions = {
    selectableRowsHideCheckboxes: true,
    responsive: 'standard',
    rowsPerPage: rowsPerPage,
    rowsPerPageOptions: [2, 10, 100, 500],
    filter: true,
    expandableRows: true,
    expandableRowsOnClick: false,
    serverSide: true,
    count: total,
    onTableChange: (action, tableState) => {
      if (dataHandler) {
        switch (action) {
          case 'changePage':
          case 'changeRowsPerPage':
          case 'sort':
            // set sortKey appropriately
            let realSortKey = 'none';
            const metricsIdx = metrics.indexOf(tableState.sortOrder.name);
            if (metricsIdx !== -1) {
              realSortKey = `results.${metricsIdx}`;
            } else {
              switch (tableState.sortOrder.name) {
                case 'Submission Date':
                  realSortKey = 'submissionDate';
                  break;
                case 'Team Name':
                  realSortKey = 'teamName';
                  break;
                case 'Submission Name':
                  realSortKey = 'name';
                  break;
                case 'Status':
                  realSortKey = 'status';
                  break;
              }
            }
            dataHandler(tableState, realSortKey);
            break;
          default:
            // console.log(`action ${action} not handled.`);
            break;
        }
      }
    },
    renderExpandableRow: (rowData: any[], rowMeta: any) => {
      const colSpan = rowData.length + 1;
      const submissionId = data[rowMeta.dataIndex].id;
      const description = data[rowMeta.dataIndex].description;
      const entry = data[rowMeta.dataIndex].entry;
      const error = data[rowMeta.dataIndex].error;
      const progress = data[rowMeta.dataIndex].progress;

      const renderProgressBar = (
        progress: SubmissionEntry['progress'],
        depth: number = 0
      ): any => {
        if (typeof progress == 'object') {
          return (
            <>
              {Object.keys(progress).map((name) => {
                if (typeof progress[name] == 'object') {
                  // if it's a object, show the progress
                  return (
                    <TreeItem
                      nodeId={name}
                      label={
                        //@ts-ignore
                        progress[name]['value'] ? (
                          <>
                            {name}{' '}
                            <span style={{ float: 'right' }}>
                              {
                                
                                Math.ceil(
                                  //@ts-ignore
                                  (progress[name]['value'] as number) * 100
                                )
                              }
                              %
                            </span>
                            <LinearProgress
                              variant="determinate"
                              value={Math.ceil(
                                //@ts-ignore
                                (progress[name]['value'] as number) * 100
                              )}
                            />
                          </>
                        ) : (
                          <span>{name}</span>
                        )
                      }
                    >
                      {
                        // @ts-ignore
                        Object.keys(progress[name]).map((name2) => {
                          if (
                            // @ts-ignore
                            typeof progress[name][name2] == 'number' &&
                            name2 !== 'value'
                          ) {
                            return (
                              <TreeItem
                                nodeId={name2}
                                label={
                                  <>
                                    {name2}{' '}
                                    <span style={{ float: 'right' }}>
                                      {
                                        Math.ceil(
                                          //@ts-ignore
                                          (progress[name][name2] as number) *
                                            100
                                        )
                                      }
                                      %
                                    </span>
                                    <LinearProgress
                                      variant="determinate"
                                      value={
                                        Math.ceil(
                                          //@ts-ignore
                                          (progress[name][name2] as number) *
                                            100
                                        )
                                      }
                                    />
                                  </>
                                }
                              />
                            );
                          } else {
                            // @ts-ignore
                            return renderProgressBar(progress[name][name2]);
                          }
                        })
                      }
                    </TreeItem>
                  );
                }
                return <></>;
              })}
            </>
          );
        }
        return <></>;
      };
      return (
        <>
          <TableRow>
            <TableCell colSpan={colSpan}>
              <Typography variant="body2">
                Submission ID: {submissionId}
              </Typography>
              <Typography variant="body2">
                Description:{' '}
                {description ? description : 'No description provided'}
              </Typography>
              {entry && (
                <Typography variant="body2">
                  Submission Data: {JSON.stringify(entry)}
                </Typography>
              )}
            </TableCell>
          </TableRow>
          {error.length > 0 && (
            <TableRow>
              <TableCell colSpan={colSpan} className="error-text">
                {error.map((err) => {
                  return <p style={{ whiteSpace: 'pre-line' }}>Error: {err}</p>;
                })}
              </TableCell>
            </TableRow>
          )}

          {showProgress && progress && (
            <TableRow>
              <TableCell colSpan={colSpan}>
                <TreeView
                  aria-label="progress bars"
                  defaultCollapseIcon={<ExpandMoreIcon />}
                  defaultExpandIcon={<ChevronRightIcon />}
                  sx={{
                    height: 240,
                    flexGrow: 1,
                    maxWidth: 400,
                    overflowY: 'auto',
                  }}
                >
                  {renderProgressBar(progress)}
                </TreeView>
              </TableCell>
            </TableRow>
          )}
        </>
      );
    },
  };
  return (
    <div className="SubmissionsList">
      <MUIDataTable
        data={tableData}
        columns={columns}
        title="Submissions"
        options={options}
      />
    </div>
  );
};

export default SubmissionsList;
